import { useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { scheduleRefresh } from '@/hooks/scrollRefresh';

gsap.registerPlugin(ScrollTrigger);

const HERO_IMG = 'https://public.readdy.ai/ai/img_res/sandbox/9375ca16a27728ad5f5dab836bff4134/8d8c8a47-00ec-4ffb-ae5c-61eed9a26856_hero.webp';

const HERO_VIDEO = 'https://storage.helloreaddy.io/project_files/63f4de25-a194-431a-b183-ebd9b578b14d/21d999e1-6e7b-4948-aff9-8c0b304c73f2_FORM_AND_FEEL_event_showreel_20261014172014_.mp4';

const DESKTOP = '(min-width: 1024px) and (prefers-reduced-motion: no-preference)';
const GAP = 28; // space between headline and media
const SIDE = 24; // matches header px-6

export default function HeroShowreel() {
  const wrap = useRef<HTMLElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const text = useRef<HTMLSpanElement>(null);
  const video = useRef<HTMLVideoElement>(null);

  const [playing, setPlaying] = useState(false);

  // Video only plays after an explicit click on the play button.
  const toggle = () => {
    const v = video.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  useLayoutEffect(() => {
    const el = wrap.current;
    const boxEl = box.current;
    const textEl = text.current;
    if (!el || !boxEl || !textEl) return undefined;

    // Fit the one-line headline exactly to the nav-aligned content width
    const fit = () => {
      textEl.style.fontSize = '100px';
      const w = textEl.scrollWidth;
      if (!w) return;
      const target = boxEl.clientWidth;
      textEl.style.fontSize = `${(100 * target) / w}px`;
    };
    // Media top edge sits just below the headline (desktop), never above 40% of viewport
    const mediaTop = () => {
      const r = boxEl.getBoundingClientRect();
      const sTop = el.getBoundingClientRect().top;
      const bottom = r.bottom - sTop + GAP;
      return Math.max(bottom, window.innerHeight * 0.4);
    };

    fit();
    const mm = gsap.matchMedia();

    mm.add(DESKTOP, () => {
      const ctx = gsap.context(() => {
        gsap.set('.hx-frame', { top: mediaTop() });
        const intro = gsap.timeline({ defaults: { ease: 'power4.out' } });
        intro.from('.hx-line', { yPercent: 110, duration: 1.2 }, 0.1)
          .from('.hx-frame-inner', { yPercent: 18, duration: 1.4, ease: 'power3.out' }, 0.25)
          .from('.hx-media-img', { scale: 1.15, duration: 1.6, ease: 'power3.out' }, 0.25);

        // Frame is taller than the visible area: viewport crops its lower part.
        // Scroll lifts it to top:0 and fills the screen. Geometry only, no transforms on frame.
        const tl = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: el,
            start: 'top top',
            end: () => `+=${window.innerHeight * 1.6}`,
            pin: true,
            scrub: 0.6,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            refreshPriority: 2,
          },
        });
        tl.fromTo('.hx-frame', {
          top: () => mediaTop(), left: SIDE, right: SIDE, borderRadius: 24,
        }, {
          top: 0, left: 0, right: 0, borderRadius: 0, duration: 0.75, ease: 'power2.inOut',
        }, 0)
          .to('.hx-head-wrap', { y: -140, opacity: 0, duration: 0.4 }, 0)
          .to('.hx-par', { scale: 1, duration: 0.75 }, 0)
          .to('.hx-caption', { opacity: 1, y: 0, duration: 0.2 }, 0.55)
          .to({}, { duration: 0.25 }); // hold fullscreen
      }, el);
      return () => ctx.revert();
    });

    mm.add('(max-width: 1023px) and (prefers-reduced-motion: no-preference)', () => {
      const ctx = gsap.context(() => {
        gsap.from('.hx-line', { yPercent: 110, duration: 1.1, ease: 'power4.out', delay: 0.1 });
        gsap.set('.hx-caption', { opacity: 1, y: 0 });
      }, el);
      return () => ctx.revert();
    });

    mm.add('(prefers-reduced-motion: reduce)', () => {
      gsap.set(el.querySelectorAll('.hx-caption'), { opacity: 1, y: 0 });
      if (window.innerWidth >= 1024) gsap.set(el.querySelector('.hx-frame'), { top: mediaTop() });
    });

    // Headline size affects mediaTop: refit before ScrollTrigger re-measures.
    const onRefreshInit = () => fit();
    ScrollTrigger.addEventListener('refreshInit', onRefreshInit);
    let alive = true;
    document.fonts?.ready.then(() => { if (alive) { fit(); scheduleRefresh(); } }).catch(() => {});
    return () => {
      alive = false;
      ScrollTrigger.removeEventListener('refreshInit', onRefreshInit);
      mm.revert();
    };
  }, []);

  return (
    <section
      id="top"
      ref={wrap}
      aria-label="Introduction"
      className="relative w-full overflow-hidden bg-[#101114] text-[#F4F3F0] flex flex-col h-[100svh] min-h-[560px] lg:block lg:min-h-0"
    >
      <div aria-hidden className="hx-glow pointer-events-none absolute -top-[25%] left-1/2 h-[60vh] w-[90vw] -translate-x-1/2 rounded-full bg-[#7C6AD6]/20 blur-[120px]" />

      <div className="relative z-10 px-4 md:px-6 pt-[92px] lg:pt-[96px]">
        <div ref={box} className="hx-head-wrap w-full">
          <h1 className="font-[Anton,'Inter_Tight',sans-serif] uppercase leading-[0.86] whitespace-nowrap">
            <span className="sr-only">FORM &amp; FEEL — creative event planning and production studio. </span>
            <span aria-hidden className="block overflow-hidden">
              <span ref={text} className="hx-line inline-block text-[100px] tracking-[-0.005em]">
                <span className="text-[#F4F3F0]">Make it </span>
                <span className="text-[#7C6AD6]">Felt.</span>
              </span>
            </span>
          </h1>
        </div>
      </div>

      {/* Media: desktop frame is 92svh tall, so its lower part is cropped by the viewport */}
      <div className="relative mt-5 md:mt-6 px-4 md:px-6 pb-4 md:pb-6 flex-1 min-h-0 lg:static lg:m-0 lg:p-0">
        <div onClick={toggle} className="hx-frame cursor-pointer relative h-full w-full isolate overflow-hidden rounded-[16px] md:rounded-[20px] lg:rounded-[24px] bg-black lg:absolute lg:z-20 lg:w-auto lg:left-6 lg:right-6 lg:top-[50svh] lg:h-[100svh]">
          <div className="hx-frame-inner absolute inset-0 overflow-hidden rounded-[inherit]">
            <div className="hx-par absolute inset-0 lg:scale-[1.06]">
              <div className="hx-media-img relative h-full w-full">
                <video
                  ref={video}
                  src={HERO_VIDEO}
                  muted
                  loop
                  playsInline
                  preload="none"
                  disablePictureInPicture
                  aria-label="FORM & FEEL event showreel: live stages, crowds and light installations"
                  className="absolute inset-0 h-full w-full object-cover object-center"
                />
                <img
                  src={HERO_IMG}
                  alt="Live event stage with crowd and violet light beams produced by FORM & FEEL"
                  title="FORM & FEEL live event production"
                  fetchPriority="high"
                  className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-500 ${playing ? 'opacity-0' : 'opacity-100'}`}
                />
              </div>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#101114]/70 via-transparent to-transparent" />
          <div className="absolute bottom-4 left-4 right-4 md:bottom-8 md:left-8 md:right-8 flex flex-wrap items-end justify-between gap-3">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggle(); }}
              aria-label={playing ? 'Pause showreel' : 'Play showreel'}
              className="group flex items-center gap-3 font-label text-[11px] md:text-xs uppercase tracking-[0.18em] whitespace-nowrap cursor-pointer"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-full border border-[#F4F3F0]/60 transition-colors group-hover:bg-[#F4F3F0] group-hover:text-[#101114]"><i className={playing ? 'ri-pause-fill' : 'ri-play-fill'} /></span>
              Experiences in motion / 01
            </button>
            <span className="hx-caption translate-y-2 opacity-0 hidden sm:block font-label text-[11px] uppercase tracking-[0.18em] text-[#DCE7F3]/80">
              Lisbon · Berlin · Amsterdam
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}